import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { formatDate } from '../../kit/time';
import { AriveLockNote, isAriveManaged, toastAriveConflict } from '../../ariveManaged';
import { toInputDate, plausible } from '../shared/dateInput';
import { Input } from '../../../components/ui/input';

const GREEN = '#6BA82E';
const AMBER = '#f59e0b';
const GREY = '#94a3b8';

// The trackers the loan officer dates by hand. ITP is the loan's own
// `intent_to_proceed_date`; the others live under `loan.trackers`.
const MANUAL = [
  { key: 'itp', label: 'ITP Status', field: 'intent_to_proceed_date' },
  { key: 'appraisal', label: 'Appraisal' },
  { key: 'hoi', label: 'HOI' },
  { key: 'title', label: 'Title' },
  { key: 'hoa', label: 'HOA Certs' },
];

function Row({ label, status, color, testId, children }) {
  return (
    <div className="px-4 py-2 flex items-center justify-between gap-3" data-testid={testId}>
      <span className="font-medium text-slate-600">{label}</span>
      <span className="flex items-center gap-2">
        {children}
        <span className="font-semibold whitespace-nowrap" style={{ color }}>{status}</span>
      </span>
    </div>
  );
}

// TRID has no date of its own: it reads the loan estimate and closing disclosure dates.
const tridStatus = (loan) => {
  if (loan.cd_issued_date) return [`CD sent ${formatDate(loan.cd_issued_date)}`, GREEN];
  if (loan.le_issued_date) return [`LE sent ${formatDate(loan.le_issued_date)}`, GREEN];
  return ['No LE yet', AMBER];
};

function ManualRow({ loan, tracker, onChange }) {
  const { key, label, field } = tracker;
  const entry = field ? { date: loan[field], not_applicable: false } : { date: loan.trackers?.[key]?.date ?? null, not_applicable: !!loan.trackers?.[key]?.not_applicable };
  const stored = toInputDate(entry.date);
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      await losApi.updateLoan(loan.loan_file_id, field
        ? { [field]: next.date }
        : { trackers: { [key]: next } });
    } catch (e) {
      if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) });
      setSaving(false);
      return;
    }
    try { if (onChange) await onChange(); } catch { /* the loan file shows its own load error */ }
    setSaving(false);
  };
  const pickDate = (e) => {
    const v = e.target.value;
    if (v === stored || (v === '' && e.target.validity?.badInput)) return;
    if (v === '' || plausible(v)) save({ ...entry, date: v || null });
  };

  let status = 'Pending';
  let color = AMBER;
  if (entry.not_applicable) { status = 'N/A'; color = GREY; }
  else if (entry.date) { status = `Done ${formatDate(entry.date)}`; color = GREEN; }

  return (
    <Row label={label} status={status} color={color} testId={`tracker-${key}`}>
      {saving && <Loader2 size={12} className="animate-spin text-slate-400" role="status" aria-label="Saving" />}
      {!entry.not_applicable && (
        <Input
          type="date"
          aria-label={`${label} date`}
          data-testid={`tracker-date-${key}`}
          className="h-7 w-auto px-1.5 text-[12px] md:text-[12px]"
          value={stored}
          disabled={saving}
          onChange={pickDate}
        />
      )}
      {!field && (
        <label className="flex items-center gap-1 text-[11px] text-slate-500">
          <input
            type="checkbox"
            data-testid={`tracker-na-${key}`}
            checked={entry.not_applicable}
            disabled={saving}
            onChange={(e) => save({ date: entry.date ? toInputDate(entry.date) : null, not_applicable: e.target.checked })}
          />
          N/A
        </label>
      )}
    </Row>
  );
}

// Arive-linked: Arive's own trackers, read-only. BFY-originated: the loan officer dates them.
export function TrackersCard({ loan, onChange }) {
  const linked = isAriveManaged(loan);
  const locked = loan.rate_lock?.is_locked;
  const [tridText, tridColor] = tridStatus(loan);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="trackers-card">
      <div className="px-4 py-3 border-b border-slate-100 text-[13px] font-semibold" style={{ color: '#030524' }}>Trackers</div>
      <div className="divide-y divide-slate-100 text-[12px]">
        {linked ? (
          <div className="px-4 py-2"><AriveLockNote loan={loan} testId="trackers-arive-note">Trackers come from Arive.</AriveLockNote></div>
        ) : (
          <Row label="TRID" status={tridText} color={tridColor} testId="tracker-trid" />
        )}
        <Row label="Lock Status" status={locked ? 'Locked' : 'Not Locked'} color={locked ? GREEN : GREY} testId="tracker-lock" />
        {linked
          ? (loan.arive.trackers || []).map((t) => (
            <Row
              key={t.name}
              label={t.label || t.name}
              status={[t.status, t.date && formatDate(t.date)].filter(Boolean).join(' · ') || '—'}
              color={t.status || t.date ? GREEN : GREY}
              testId={`tracker-arive-${t.name}`}
            />
          ))
          : MANUAL.map((t) => <ManualRow key={t.key} loan={loan} tracker={t} onChange={onChange} />)}
        {linked && (loan.arive.trackers || []).length === 0 && (
          <div className="px-4 py-3 text-slate-400 italic" data-testid="trackers-arive-empty">Arive has not sent any trackers for this loan yet.</div>
        )}
      </div>
    </div>
  );
}
